import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import dayjs from "dayjs";

import { useAuthStore } from "../store/useAuthStore";
import { useEsimCollection } from "../hooks/useEsimCollection";
import type { EsimModel } from "../repositories/esim.repository";
import ModalOrderDetail from "../components/modals/ModalOrderDetail";
import ModalActiveESim from "../components/modals/ModalActiveESim";

import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import { red } from "@mui/material/colors";

/* ——————————————————— Types ——————————————————— */

export interface EsimPlan {
  planName: string;
  msisdn: string;
  status: string;
  expiresAt: string;
  qrData: string;
}

/* ——————————————————— Theme ——————————————————— */

const theme = createTheme({
  palette: {
    primary: { main: "#D32F2F" },
    background: { default: "#fafafa" },
    divider: alpha("#111827", 0.08),
  },
  typography: {
    fontFamily: "Inter, system-ui, Roboto",
    h6: { fontWeight: 800 },
    button: { textTransform: "none", fontWeight: 600 },
  },
});

/* ——————————————————— Utils ——————————————————— */

const toPlan = (e: EsimModel): EsimPlan => ({
  planName: e.packageName,
  msisdn: e.msisdn,
  status: e.status,
  expiresAt: e.activeStop,
  qrData: e.esimQrCode,
});

const StatusChip = ({ status }: { status: string }) => (
  <Chip
    size="small"
    label={status}
    color={
      status === "Waiting"
        ? "warning"
        : status === "Active"
          ? "success"
          : "error"
    }
    sx={{ fontWeight: 600 }}
  />
);

/* ——————————————————— Components ——————————————————— */

const TabPanel: React.FC<{
  value: number;
  index: number;
  children: React.ReactNode;
}> = ({ value, index, children }) =>
  value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const EsimCard: React.FC<{
  plan: EsimPlan;
  onInstall: (p: EsimPlan) => void;
  onActivate: (p: EsimPlan) => void;
}> = ({ plan, onInstall, onActivate }) => {
  const expiry = dayjs(plan.expiresAt);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 6,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontSize={15} fontWeight={600}>
              {plan.planName}
            </Typography>

            <StatusChip status={plan.status} />
          </Stack>
        </Box>

        <Divider variant="middle" />
        <Box
          className="bottom-section"
          sx={{
            px: 3,
            pt: 2.5,
          }}
        >
          <Stack spacing={2}>
            {/* MSISDN */}

            <Typography
              fontSize={14}
              fontWeight={600}
              color={"text.primary"}
              sx={{
                letterSpacing: 0.6,
                fontFamily: "monospace",
                lineHeight: 1.2,
              }}
            >
              MSISDN / IMSI: {plan.msisdn}
            </Typography>

            {/* Expiry */}
            {expiry.isValid() && (
              <Typography fontSize={12} color="text.secondary">
                Expires on {expiry.format("DD MMM YYYY")}
              </Typography>
            )}

            {/* Actions */}
            <Stack direction="row" spacing={1.5} pt={1}>
              {plan.status !== "Expired" && plan.status !== "Active" ? (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<DownloadRoundedIcon />}
                    onClick={() => onInstall(plan)}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Install
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PowerSettingsNewRoundedIcon />}
                    onClick={() => onActivate(plan)}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Activate
                  </Button>
                </>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<RemoveRedEyeRoundedIcon />}
                  onClick={() => onInstall(plan)}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  View eSIM
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ——————————————————— Page ——————————————————— */

const MyEsimPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState<EsimPlan | null>(null);
  const [selectedActive, setSelectedActive] = useState<EsimPlan | null>(null);

  const { user } = useAuthStore();
  const cusId = user?.cusId ?? "";

  const {
    data: eSimData,
    isLoading: isLoadingEsim,
    error,
    refetch: refetchEsim,
    isFetching,
  } = useEsimCollection(cusId, { enabled: !!cusId });

  const activeEsims = useMemo(
    () => (eSimData?.active ?? []).map((e) => toPlan(e)),
    [eSimData],
  );

  const expiredEsims = useMemo(
    () => (eSimData?.expired ?? []).map((e) => toPlan(e)),
    [eSimData],
  );

  return (
    <ThemeProvider theme={theme}>
      <Box minHeight="100vh">
        <Container maxWidth="sm" sx={{ py: 3 }}>
          {error && <Alert severity="error">Failed to load eSIMs</Alert>}

          <Paper
            sx={{
              mb: 1,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="fullWidth"
            >
              <Tab label={`Available (${activeEsims.length})`} />
              <Tab label={`Expired (${expiredEsims.length})`} />
            </Tabs>
          </Paper>

          <TabPanel value={tab} index={0}>
            <Stack spacing={1.5}>
              {isLoadingEsim || isFetching ? (
                <>
                  <EsimCardSkeleton />
                  <EsimCardSkeleton />
                  <EsimCardSkeleton />
                </>
              ) : (
                activeEsims.map((p) => (
                  <EsimCard
                    key={p.msisdn}
                    plan={p}
                    onInstall={setSelected}
                    onActivate={setSelectedActive}
                  />
                ))
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Stack spacing={1.5}>
              {isLoadingEsim || isFetching ? (
                <>
                  <EsimCardSkeleton />
                  <EsimCardSkeleton />
                  <EsimCardSkeleton />
                </>
              ) : (
                expiredEsims.map((p) => (
                  <EsimCard
                    key={p.msisdn}
                    plan={p}
                    onInstall={setSelected}
                    onActivate={() => {}}
                  />
                ))
              )}
            </Stack>
          </TabPanel>
        </Container>

        <ModalOrderDetail
          open={!!selected}
          plan={selected}
          onClose={() => setSelected(null)}
        />

        <ModalActiveESim
          open={!!selectedActive}
          plan={selectedActive}
          onActive={refetchEsim}
          onClose={() => setSelectedActive(null)}
        />
      </Box>
    </ThemeProvider>
  );
};

const EsimCardSkeleton: React.FC = () => {
  return (
    <Card
      elevation={0}
      sx={{
        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        borderRadius: 6,
        p: 1,
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 4 }}>
          <Stack spacing={1} flex={1}>
            <Box
              sx={{
                width: "60%",
                height: 20,
                bgcolor: alpha("#111827", 0.08),
                borderRadius: 1,
              }}
            />
            <Box
              sx={{
                width: "40%",
                height: 16,
                bgcolor: alpha("#111827", 0.05),
                borderRadius: 1,
              }}
            />
          </Stack>
          <Box
            sx={{
              width: 70,
              height: 24,
              bgcolor: alpha("#111827", 0.08),
              borderRadius: 3,
            }}
          />
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Box
          sx={{
            width: "50%",
            height: 16,
            bgcolor: alpha("#111827", 0.06),
            borderRadius: 1,
            mt: 1,
          }}
        />

        <Stack direction="row" spacing={1} mt={2}>
          <Box
            sx={{
              flex: 1,
              height: 36,
              bgcolor: alpha("#111827", 0.08),
              borderRadius: 10,
            }}
          />
          <Box
            sx={{
              flex: 1,
              height: 36,
              bgcolor: alpha("#111827", 0.05),
              borderRadius: 10,
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};
export default MyEsimPage;
